import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface UserState {
  userId: number | null
  username: string | null
  email: string | null
  firstName: string | null
  lastName: string | null
  role: string | null
  permissions: string[]
  /** Institución del usuario (p. ej. ADMIN); null si no aplica. */
  institutionId: number | null
  token: string | null
  isAuthenticated: boolean
}

interface AuthStore extends UserState {
  setUser: (
    user: Omit<UserState, 'isAuthenticated'> & { token: string; refreshToken?: string | null }
  ) => void
  logout: () => void
  getFullName: () => string
}

const initialState: UserState = {
  userId: null,
  username: null,
  email: null,
  firstName: null,
  lastName: null,
  role: null,
  permissions: [],
  institutionId: null,
  token: null,
  isAuthenticated: false,
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      setUser: ({ refreshToken, ...user }) => {
        if (user.token) localStorage.setItem('token', user.token)
        if (refreshToken) localStorage.setItem('refreshToken', refreshToken)
        set({
          ...user,
          institutionId: user.institutionId ?? null,
          isAuthenticated: !!user.token,
        })
      },
      logout: () => {
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        set(initialState)
      },
      getFullName: () => {
        const { firstName, lastName } = get()
        return [firstName, lastName].filter(Boolean).join(' ') || 'Usuario'
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        userId: state.userId,
        username: state.username,
        email: state.email,
        firstName: state.firstName,
        lastName: state.lastName,
        role: state.role,
        permissions: state.permissions,
        institutionId: state.institutionId,
        isAuthenticated: state.isAuthenticated,
      }),
      merge: (persisted, current) => {
        const p = persisted as Partial<UserState> | undefined
        return {
          ...current,
          ...p,
          institutionId: p?.institutionId ?? null,
        }
      },
    }
  )
)
